/**
 * AstorToolRenderers — Status Color Utility
 *
 * Maps free-form status strings (returned by various Astor tools) to a
 * GlassChip color variant. Tools may use different status spellings
 * (PENDING, pending, in_review, etc.) — we normalise here so every renderer
 * shares the same mapping.
 *
 * Issue #1432 Phase 2
 */

export type StatusChipColor = 'success' | 'warning' | 'error' | 'info' | 'default';

const STATUS_COLOR_MAP: Record<string, StatusChipColor> = {
  // Success — closed, approved, active deals
  active: 'info',
  approved: 'success',
  closed: 'success',
  completed: 'success',
  funded: 'success',
  qualified: 'success',
  won: 'success',

  // Warning — pending, in review, partial
  pending: 'warning',
  in_review: 'warning',
  inreview: 'warning',
  reviewing: 'warning',
  partial: 'warning',
  draft: 'warning',
  awaiting: 'warning',
  hold: 'warning',
  on_hold: 'warning',

  // Error — rejected, cancelled, lost
  rejected: 'error',
  declined: 'error',
  cancelled: 'error',
  canceled: 'error',
  failed: 'error',
  lost: 'error',
  expired: 'error',

  // Info — new, open, in progress
  new: 'info',
  open: 'info',
  in_progress: 'info',
  inprogress: 'info',
  processing: 'info',
};

/**
 * Map an arbitrary status string to a chip color. Unknown statuses fall
 * back to 'default' so the chip still renders without throwing.
 */
export function getStatusColor(status: string | undefined | null): StatusChipColor {
  if (!status) return 'default';
  const key = String(status)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return STATUS_COLOR_MAP[key] ?? 'default';
}

/**
 * Convert a status code (e.g. "IN_REVIEW", "in_review") to a human-friendly
 * display label ("In Review"). Used as a fallback when no i18n label exists
 * for a given status.
 */
export function formatStatusLabel(status: string | undefined | null): string {
  if (!status) return '';
  return String(status)
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
